import { ShootingStatus, ShootingType } from '../domain/types';
import { shootingStatusLabels } from '../labels';
import { Photographer, PhotographerSearchResult, Shooting } from '../providers/api/dtos';
import { formatInputDate, formatInputTime, formDateTimeToDate } from '../utils/form';
import { UPSERT_SHOOTING_FORM_DEFAULT_VALUES, UpsertShootingFormModel } from './components/upsert-shooting-form/UpsertShootingForm';
import {
  colorsTypeAgent, colorsTypeDirectOrPartner, iconsTypeAgent, iconsTypeDirectOrPartner, NO_VIRTUAL_TOUR,
} from './constants';
import { ComputedShootingStatus } from './hooks/use-computed-shooting-status/useComputedShootingStatus';

const toPhotographerSearchResult = (photographer: Photographer): PhotographerSearchResult => {
  const fullName = `${photographer.name} ${photographer.surname}`.trim();

  return {
    id: photographer.id,
    name: fullName,
    fullName,
  };
};

export const shootingToUpsertShootingFormModel = (
  shooting?: Shooting,
  photographer?: Photographer,
): UpsertShootingFormModel => {
  if (!shooting) return UPSERT_SHOOTING_FORM_DEFAULT_VALUES;

  return {
    type: shooting.type ?? '',
    date: formatInputDate(new Date(shooting.dueDate)),
    time: formatInputTime(new Date(shooting.dueDate)),
    photoPrice: shooting.price?.photo?.toString() ?? '',
    extraServicesPrice: shooting.price?.extraServices?.toString() ?? '',
    expensesRefund: shooting.price?.refund?.toString() ?? '',
    virtualTourType: shooting.virtualTourType ?? NO_VIRTUAL_TOUR,
    deliveryDate: shooting.deliveryDate
      ? formatInputDate(new Date(shooting.deliveryDate))
      : '',
    notes: shooting.notes ?? '',
    photographer: photographer ? toPhotographerSearchResult(photographer) : undefined,
  };
};

export const upsertShootingFormModelToShooting = (
  formValues: UpsertShootingFormModel,
  existingShooting: Shooting,
): Shooting => ({
  ...existingShooting,
  type: formValues.type as ShootingType,
  photographerId: formValues.photographer?.id || undefined,
  dueDate: formDateTimeToDate(formValues.date, formValues.time).toISOString(),
  deliveryDate: formValues.deliveryDate ? new Date(formValues.deliveryDate).toISOString() : undefined,
  virtualTourType: formValues.virtualTourType && formValues.virtualTourType !== NO_VIRTUAL_TOUR
    ? formValues.virtualTourType
    : undefined,
  price: {
    photo: formValues.photoPrice.trim() ? parseFloat(formValues.photoPrice.trim()) : undefined,
    extraServices: formValues.extraServicesPrice.trim() ? parseFloat(formValues.extraServicesPrice.trim()) : undefined,
    refund: formValues.expensesRefund.trim() ? parseFloat(formValues.expensesRefund.trim()) : undefined,
  },
  notes: formValues.notes.trim() || undefined,
});

export function computeShootingStatusStyles(
  shootingStatus: ComputedShootingStatus,
  shootingType: ShootingType,
  userIsAdminContentOrAgent: boolean,
) {
  if (shootingStatus === 'NO_STATUS') {
    return {
      icon: undefined,
      color: undefined,
      label: undefined,
    };
  }

  if (userIsAdminContentOrAgent && [ShootingType.DIRECT, ShootingType.PARTNER].includes(shootingType)) {
    return {
      icon: iconsTypeDirectOrPartner[shootingStatus],
      color: colorsTypeDirectOrPartner[shootingStatus],
      label: shootingStatusLabels[shootingStatus],
    };
  }

  if (userIsAdminContentOrAgent && shootingType === ShootingType.AGENT) {
    return {
      icon: iconsTypeAgent[shootingStatus] || undefined,
      color: colorsTypeAgent[shootingStatus] || undefined,
      // eslint-disable-next-line no-nested-ternary
      label: (() => {
        switch (shootingStatus) {
          case ShootingStatus.DELIVERED:
          case ShootingStatus.COMPLETED:
            return 'Foto agente';
          case ShootingStatus.CLOSED:
            return shootingStatusLabels[shootingStatus];
          default:
            return undefined;
        }
      })(),
    };
  }

  return {
    icon: undefined,
    color: undefined,
    label: undefined,
  };
}
